/* assets/littleg-kid-active-state.js — LITTLEG-FRIEND-MAP-ACTIVE-STATE-12
 * Read-only UI state projection owner for /pages/kid-map.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Map runtime & marker lifecycle: assets/littleg-kid-map.js
 * - Privacy location transformation: assets/littleg-kid-location-privacy.js (V1.3.0)
 * - Family social data storage: LittleGFamilyData.socialGraph
 * - Liquid DOM owner: sections/littleg-kid-map-workspace.liquid
 * - THIS FILE OWNS: Read-only UI state projection (dynamic marker palette decoration,
 *   passport count badge visibility) via non-destructive DOM observation & requestAnimationFrame.
 */
(function (window, document) {
  'use strict';

  var VERSION = '1.12.0';
  var ROOT_SELECTOR = '[data-littleg-kid-map]';
  var PALETTE = Object.freeze(['#ffe08a', '#a8ddff', '#b8ef9b', '#ffd0d5', '#d9c7ff', '#ffd29b']);

  function q(root, selector) {
    return root ? root.querySelector(selector) : null;
  }
  function qa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function hash(value) {
    var source = String(value || 'friend');
    var out = 0;
    for (var i = 0; i < source.length; i += 1) {
      out = ((out << 5) - out + source.charCodeAt(i)) | 0;
    }
    return Math.abs(out);
  }

  function syncPassportCount(root) {
    var countNode = q(root, '[data-kid-stamp-count]');
    if (!countNode) return;

    var value = Number.parseInt(String(countNode.textContent || '').trim(), 10);
    var hasCount = Number.isFinite(value) && value > 0;

    countNode.hidden = !hasCount;

    var passportLink = q(root, '[data-kid-passport-link]');
    if (passportLink) {
      passportLink.setAttribute('data-kid-passport-has-count', hasCount ? 'true' : 'false');
    }
  }

  function decorateMarkers(root) {
    if (!(root instanceof HTMLElement)) return;

    // 1. Direct registry check if populated by kid-map.js
    var markerRegistry = root.__kidSocialMarkersById;
    if (markerRegistry) {
      Object.keys(markerRegistry).forEach(function (socialId, index) {
        var marker = markerRegistry[socialId];
        if (!marker || typeof marker.getElement !== 'function') return;

        var element = marker.getElement();
        var pin = element && q(element, '.kid-custom-pin');
        if (!pin) return;

        var isMine = pin.classList.contains('kid-custom-pin--mine');
        var color = isMine ? '#dff6ea' : PALETTE[hash(socialId || index) % PALETTE.length];
        pin.style.setProperty('--kid-pin-color', color);
      });
    }

    // 2. Fallback DOM scan for newly rendered custom pins in canvas
    qa(root, '.kid-custom-pin').forEach(function (pin, index) {
      var shell = pin.closest('.kid-custom-pin-shell');
      var socialId = shell ? shell.getAttribute('data-social-id') || shell.id : String(index);
      var isMine = pin.classList.contains('kid-custom-pin--mine');
      var color = isMine ? '#dff6ea' : PALETTE[hash(socialId) % PALETTE.length];
      pin.style.setProperty('--kid-pin-color', color);
    });
  }

  var rafPending = false;
  function scheduleRefresh(root) {
    if (rafPending) return;
    rafPending = true;
    window.requestAnimationFrame(function () {
      rafPending = false;
      if (!(root instanceof HTMLElement)) return;
      syncPassportCount(root);
      decorateMarkers(root);
    });
  }

  function observeCanvasMutations(root) {
    if (!root || root.__kidActiveStateObserver || typeof MutationObserver !== 'function') return;

    var canvasHost = q(root, '[data-gproperty-map-canvas]') || root;

    var observer = new MutationObserver(function (records) {
      var hasPinMutation = records.some(function (record) {
        return Array.prototype.slice.call(record.addedNodes || []).some(function (node) {
          if (!node || node.nodeType !== 1) return false;
          return (
            (node.matches && node.matches('.kid-custom-pin-shell, .kid-custom-pin')) ||
            (node.querySelector && node.querySelector('.kid-custom-pin-shell, .kid-custom-pin'))
          );
        });
      });

      if (hasPinMutation) {
        scheduleRefresh(root);
      }
    });

    observer.observe(canvasHost, { childList: true, subtree: true });
    root.__kidActiveStateObserver = observer;
  }

  function observePassportCount(root) {
    var countNode = q(root, '[data-kid-stamp-count]');
    if (!countNode || countNode.__kidStampObserver || typeof MutationObserver !== 'function')
      return;

    var observer = new MutationObserver(function () {
      syncPassportCount(root);
    });

    observer.observe(countNode, { childList: true, subtree: true, characterData: true });
    countNode.__kidStampObserver = observer;
  }

  function bind(root) {
    if (!root || root.__kidActiveStateVersion === VERSION) return;
    root.__kidActiveStateVersion = VERSION;

    observeCanvasMutations(root);
    observePassportCount(root);

    // Read-only event listeners for UI state sync
    document.addEventListener('littleg:family-social-graph-change', function () {
      scheduleRefresh(root);
    });

    document.addEventListener('littleg:kid-map:station-selected', function () {
      scheduleRefresh(root);
    });

    root.addEventListener('click', function () {
      window.setTimeout(function () {
        scheduleRefresh(root);
      }, 0);
    });

    scheduleRefresh(root);

    window.setTimeout(function () {
      scheduleRefresh(root);
    }, 240);
  }

  function mountAll() {
    qa(document, ROOT_SELECTOR).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', mountAll);

  window.LittleGFriendMapActiveState = Object.freeze({
    version: VERSION,
    mount: mountAll,
    refresh: function () {
      qa(document, ROOT_SELECTOR).forEach(scheduleRefresh);
    },
  });
})(window, document);
